/**
 * V5 报告 raw payload → 前端 view model。
 *
 * **后端权威 wrapper 形状**（docs/archive/v5/M5-integration-contract.md §API Contract，
 * 2026-05-20 backend handoff 锁定）：
 *
 *   HTTP body:
 *     {
 *       status: 'done' | 'generating' | 'failed',
 *       report_version: 5,
 *       is_fallback: bool,           // top-level mirror of meta.is_fallback
 *       user_report_v5: { ... } | null
 *     }
 *
 *   user_report_v5:
 *     - meta:     {report_version, assessment_id, depth, depth_label,
 *                  answered_count, total_questions, generated_at, model, is_fallback}
 *     - user:     {nickname, position, industry, ai_usage_purpose}
 *     - overall:  {average, l_grade, l_grade_label, percentage}
 *     - abilities[8]: {code, name, layer, layer_label, layer_sort,
 *                      average, l_grade, l_grade_label, percentage}
 *     - layers:   {<layer_code>: {name, average, l_grade, percentage}} × 3
 *     - traits[3]:    {code, name, average, normalized, tier, narrative}
 *     - 8 LLM 字段：headline_tag / overall_summary / strength_abilities /
 *                  improvement_abilities / priority_actions /
 *                  comprehensive_interpretation / try_first_scenarios /
 *                  be_cautious_scenarios / priority_improvements
 *     - quality:  {is_fallback, validator_failures: string[]}
 *
 * Adapter 行为：
 *  - 把上述结构平摊成 view model（见 createEmptyViewModel）。
 *  - 缺字段 → 给空值/fallback；不抛异常（防御性）。
 *  - 后端如果 ability/trait 已自带 name / l_grade_label，前端不查 constants；
 *    只在缺 name（旧报告 / mock 不完整）时退回到 reportV5Labels.js。
 *  - detailed.unlocked 在 V5 wrapper 里不存在；保留 view model 里的字段为
 *    UI 占位（永远 false），等付费报告单独 milestone 接入。
 */
import {
  ABILITY_LABELS,
  ABILITY_ORDER,
  LAYER_LABELS,
  LAYER_ORDER,
  TRAIT_LABELS,
  TRAIT_ORDER,
  L_GRADE_LABELS,
  DEPTH_VERSION_LABELS,
  TIER_ALIASES,
} from '../constants/reportV5Labels';

// ---- helpers ---------------------------------------------------------------
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

const FALLBACK_USER_VALUES = new Set(['未填写', '匿名测评者', '']);
function cleanUserValue(v) {
  if (!v || FALLBACK_USER_VALUES.has(v)) return '';
  return v;
}

function abilityName(code, raw) {
  return raw?.name || ABILITY_LABELS[code]?.name || code;
}
function abilityLGradeLabel(item) {
  return item?.l_grade_label || L_GRADE_LABELS[item?.l_grade] || '';
}
function traitName(code, raw) {
  return raw?.name || TRAIT_LABELS[code]?.name || code;
}
function traitIcon(code) {
  return TRAIT_LABELS[code]?.icon || 'circle';
}
function layerName(code, raw) {
  return raw?.name || LAYER_LABELS[code]?.name || code;
}
function depthVersionLabel(depth, label) {
  return label || DEPTH_VERSION_LABELS[depth] || depth || '基础版';
}
function normalizeTier(tier) {
  if (!tier) return '';
  return TIER_ALIASES[tier] || tier;
}

// ---- empty / fallback view model ------------------------------------------
function createEmptyViewModel() {
  return {
    meta: {
      nickname: '匿名测评者',
      position: '',
      industry: '',
      ai_usage_purpose: '',
      version_label: '',
      answers_done: 0,
      answers_total: 0,
      generated_at: '',
      model: '',
    },
    overall: {
      l_grade: '',
      l_grade_label: '',
      percentage: 0,
      summary: '',
      headline_tag: '',
    },
    abilities: [],
    abilities_by_layer: [],
    strength_abilities: [],
    improvement_abilities: [],
    traits: [],
    priority_actions: [],
    comprehensive_interpretation: '',
    try_first_scenarios: [],
    be_cautious_scenarios: [],
    priority_improvements: [],
    is_fallback: false,
    validator_failures: [],
    detailed: { unlocked: false },
  };
}

// ---- main -----------------------------------------------------------------
/**
 * 把后端 HTTP body 转成稳定的 view model。
 *
 * @param {object} body  - 整个 HTTP response body（顶层有 status / report_version /
 *                          is_fallback / user_report_v5）。也接受直接传 user_report_v5
 *                          内部对象（adapter 自动检测）。
 * @returns {object} view model
 */
export function toReportV5ViewModel(body) {
  const vm = createEmptyViewModel();
  if (!body || typeof body !== 'object') return vm;

  // 兼容两种入参：完整 HTTP body 或 user_report_v5 内部对象
  const inner = body.user_report_v5 && typeof body.user_report_v5 === 'object'
    ? body.user_report_v5
    : body;
  if (!inner || typeof inner !== 'object') return vm;

  const meta = inner.meta || {};
  const user = inner.user || {};
  const overall = inner.overall || {};
  const abilitiesRaw = Array.isArray(inner.abilities) ? inner.abilities : [];
  const layersRaw = inner.layers && typeof inner.layers === 'object' ? inner.layers : {};
  const traitsRaw = Array.isArray(inner.traits) ? inner.traits : [];
  const quality = inner.quality || {};

  // --- meta + user ---
  vm.meta = {
    nickname: cleanUserValue(user.nickname) || '匿名测评者',
    position: cleanUserValue(user.position),
    industry: cleanUserValue(user.industry),
    ai_usage_purpose: cleanUserValue(user.ai_usage_purpose),
    version_label: depthVersionLabel(meta.depth, meta.depth_label),
    answers_done: Number(meta.answered_count) || 0,
    answers_total: Number(meta.total_questions) || 0,
    generated_at: formatDate(meta.generated_at),
    model: meta.model || '',
  };

  // --- overall ---
  vm.overall = {
    l_grade: overall.l_grade || '',
    l_grade_label: overall.l_grade_label || L_GRADE_LABELS[overall.l_grade] || '',
    percentage: Number.isFinite(overall.percentage) ? overall.percentage : 0,
    summary: inner.overall_summary || '',
    headline_tag: inner.headline_tag || '',
  };

  // --- abilities (canonical sort) ---
  const abilityByCode = new Map(abilitiesRaw.map((a) => [a?.code, a]));
  vm.abilities = ABILITY_ORDER.map((code) => {
    const a = abilityByCode.get(code) || {};
    return {
      code,
      name: abilityName(code, a),
      layer: a.layer || ABILITY_LABELS[code]?.layer || '',
      layer_sort: a.layer_sort ?? ABILITY_LABELS[code]?.layer_sort ?? 0,
      percentage: Number.isFinite(a.percentage) ? a.percentage : 0,
      l_grade: a.l_grade || '',
      l_grade_label: abilityLGradeLabel(a),
    };
  });

  // --- abilities_by_layer ---
  vm.abilities_by_layer = LAYER_ORDER.map((layerCode) => ({
    layer: layerCode,
    layer_name: layerName(layerCode, layersRaw[layerCode]),
    abilities: vm.abilities
      .filter((x) => x.layer === layerCode)
      .sort((x, y) => (x.layer_sort || 0) - (y.layer_sort || 0)),
  }));

  // --- strength / improvement summaries ---
  vm.strength_abilities = (inner.strength_abilities || [])
    .slice(0, 3)
    .map((code) => ({ code, name: abilityName(code, abilityByCode.get(code)) }));
  vm.improvement_abilities = (inner.improvement_abilities || [])
    .slice(0, 3)
    .map((code) => ({ code, name: abilityName(code, abilityByCode.get(code)) }));

  // --- traits (canonical order) ---
  const traitByCode = new Map(traitsRaw.map((t) => [t?.code, t]));
  vm.traits = TRAIT_ORDER.map((code) => {
    const t = traitByCode.get(code) || {};
    return {
      code,
      name: traitName(code, t),
      tier: normalizeTier(t.tier) || '中等水平',
      narrative: t.narrative || '',
      icon: traitIcon(code),
    };
  });

  // --- LLM lists ---
  vm.priority_actions = (inner.priority_actions || []).slice(0, 3);
  vm.comprehensive_interpretation = inner.comprehensive_interpretation || '';
  vm.try_first_scenarios = (inner.try_first_scenarios || []).slice(0, 6);
  vm.be_cautious_scenarios = (inner.be_cautious_scenarios || []).slice(0, 6);
  vm.priority_improvements = (inner.priority_improvements || []).slice(0, 3);

  // --- fallback flag —— 2026-05-21 拆分：粗略 banner 只看 rough，详细 banner 看 detailed ---
  // body.is_fallback 后端语义已收窄到只反映粗略；quality.is_fallback / meta.is_fallback 同源。
  vm.is_fallback = Boolean(
    body.is_fallback ?? quality.is_fallback ?? meta.is_fallback,
  );
  // detailed fallback：顶层新增字段（后端 2026-05-21+），降级回 quality.detailed_is_fallback。
  vm.is_detailed_fallback = Boolean(
    body.is_detailed_fallback ?? quality.detailed_is_fallback ?? false,
  );
  vm.validator_failures = Array.isArray(quality.validator_failures)
    ? quality.validator_failures.slice()
    : [];

  // detailed unlock 从顶层 body.is_detailed_unlocked 读（backend 2026-05-20 14:30 ack 后落地）。
  // 粗略 endpoint 总是返回这个字段；为 true 时粗略报告底部 CTA 显示"查看详细报告"，否则"解锁详细报告"。
  vm.detailed = { unlocked: Boolean(body?.is_detailed_unlocked) };

  return vm;
}

// 供单测 / Storybook 使用
export const __internal = { createEmptyViewModel, formatDate, normalizeTier, cleanUserValue };
